import type { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { VariantImageAltText } from 'autogen/translation-keys/trans-website-productvariantpicker';
import Image from 'components/generic/Image';
import LinkAction from 'components/shared/Routing/components/LinkAction/LinkAction';
import imageOverlayStyle from 'components/shared/style/imageOverlay';
import { format } from 'modules/helpers/strings';
import DiagonalLines from './DiagonalLines';
import { baseOutline, selectedStyling, swatchStateStyling } from './shared/styles';
import type { ImageSwatchProps, SwatchSize } from './shared/types';

const commonWrapperStyles = css<{ $isSelected: boolean; $size: SwatchSize }>`
    position: relative;
    display: block;
    background-color: #ffffff; // same color as product image's whitespace

    ${({ $size }) => {
        switch ($size) {
            case 'xs':
            case 'sm':
                return css`
                width: 56px;
                height: 72px;
            `;
            case 'lg':
            default:
                return css`
                width: 94px;
                height: 125px;
            `;
        }
    }}
    ${baseOutline}
    ${({ $isSelected }) => $isSelected && selectedStyling('lg')}
    ${swatchStateStyling('lg')}

    &:before {
        content: '';
        z-index: 1;
        ${imageOverlayStyle}
    }
`;

const Wrapper = styled.div<{ $isSelected: boolean; $size: SwatchSize }>`
    ${commonWrapperStyles}
`;

const LinkWrapper = styled(LinkAction)<{ $isSelected: boolean; $size: SwatchSize }>`
    ${commonWrapperStyles}
`;

const Img = styled(Image.WithScaling)`
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: ${({ theme }) => theme.Spacing4};
`;

export default function ImageSwatch({
    image,
    name,
    isSelected,
    unavailable,
    linkAction,
    onClick,
    size = 'sm',
    ...restProps
}: ImageSwatchProps & HTMLAttributes<HTMLSpanElement>) {
    const { t } = useTranslation();

    if (!image) return null;

    const content = (
        <>
            <Img
                src={image}
                sizes={size === 'lg' ? 94 : 56}
                alt={format(t(VariantImageAltText))(name) || ''}
                title={name}
                transformType="SquareScale"
                imageType="product"
            />
            {unavailable && <DiagonalLines size={size} />}
        </>
    );

    if (onClick) {
        return (
            <Wrapper
                aria-label={name}
                $isSelected={isSelected}
                $size={size}
                onClick={onClick}
                as="button"
                {...restProps}
            >
                {content}
            </Wrapper>
        );
    }

    if (!linkAction) {
        return (
            <Wrapper aria-label={name} $isSelected={isSelected} $size={size} {...restProps}>
                {content}
            </Wrapper>
        );
    }

    return (
        <LinkWrapper action={linkAction} aria-label={name} $isSelected={isSelected} $size={size} {...restProps}>
            {content}
        </LinkWrapper>
    );
}
